export interface ComposerControls {
  /** Disables the input and send (button and Enter); undefined when typing is allowed. */
  inputDisabledMessage: string | undefined;
  showStop: boolean;
}

/**
 * `disabledMessage` (connecting, closed) shuts the whole composer. `lockedMessage`
 * (e.g. the turn waits on an approval) blocks typing and sending but keeps Stop,
 * so the user can still end the turn.
 */
export const composerControls = ({
  disabledMessage,
  lockedMessage,
  isStreaming,
}: {
  disabledMessage?: string;
  lockedMessage?: string;
  isStreaming: boolean;
}): ComposerControls => ({
  inputDisabledMessage: disabledMessage ?? lockedMessage,
  showStop: isStreaming && !disabledMessage,
});

/** A message send is refused while a restore runs or the caller's own lock holds. */
export const isBuilderSendBlocked = (
  restoringSnapshotId: string | null,
  isBlocked?: () => boolean
): boolean => restoringSnapshotId !== null || (isBlocked?.() ?? false);

export interface ComposerSendResult {
  value: string;
  notSent: boolean;
}

export const resolveComposerSend = (
  text: string,
  sent: boolean
): ComposerSendResult =>
  sent ? { value: '', notSent: false } : { value: text, notSent: true };

export interface ComposerDraft {
  id: string;
  text: string;
}

export const draftsAfter = (
  drafts: readonly ComposerDraft[],
  lastTakenId: string | null
): ComposerDraft[] => {
  const index =
    lastTakenId === null
      ? -1
      : drafts.findIndex((draft) => draft.id === lastTakenId);
  return drafts.slice(index + 1);
};

export const withReturnedText = (current: string, returned: string): string =>
  current.trim() ? `${returned}\n${current}` : returned;

export const withoutSentText = (current: string, sent: string): string => {
  if (!sent) return current;
  let at = current.lastIndexOf(`\n${sent}`);
  at = at === -1 ? (current.startsWith(sent) ? 0 : -1) : at + 1;
  if (at === -1) return current;
  const before = current.slice(0, at).replace(/\n$/, '');
  const after = current.slice(at + sent.length).trimStart();
  return before && after ? `${before}\n${after}` : before || after;
};
